import React from 'react';
import { LayoutGrid, Palette, PencilLine, FolderOpen, Share2, HelpCircle, Settings, User } from 'lucide-react';

interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onShowSystemIntro?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeTab, onTabChange }) => {
  const menuItems = [
    { id: 'marketplace', label: '广场', icon: LayoutGrid },
    { id: 'works', label: '作品', icon: Palette },
    { id: 'create', label: '创作', icon: PencilLine },
    { id: 'library', label: '资料库', icon: FolderOpen },
    { id: 'share', label: '分享', icon: Share2 },
    { id: 'help', label: '帮助', icon: HelpCircle },
    { id: 'settings', label: '设置', icon: Settings },
  ];

  return (
    <div className="w-16 md:w-20 h-full bg-white/80 backdrop-blur-md flex flex-col items-center py-6 border-r border-pink-100 z-50 transition-all shadow-[4px_0_24px_rgba(0,0,0,0.02)] flex-shrink-0">
       {/* 系统介绍 Logo - 顶部 */}
       <div className="mb-4">
         <button 
           onClick={() => onTabChange('intro')}
           className={`group relative transition-all duration-300 ${activeTab === 'intro' ? 'scale-110' : 'hover:scale-110'}`}
           title="关于系统"
         >
           <div className={`p-2.5 rounded-2xl mb-1 transition-all duration-300 flex items-center justify-center ${
             activeTab === 'intro' 
               ? 'bg-[#ff5c5c] shadow-[0_8px_16px_rgba(255,92,92,0.25)]' 
               : 'group-hover:bg-pink-50'
           }`}>
             <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-transparent">
               <img 
                 src="/logo.png" 
                 alt="Logo" 
                 className="w-full h-full object-contain"
                 style={{ 
                   clipPath: 'circle(50% at center)',
                   WebkitClipPath: 'circle(50% at center)'
                 }}
                 onError={(e) => {
                   (e.target as HTMLImageElement).src = 'https://raw.githubusercontent.com/lucide-icons/lucide/main/icons/palette.svg';
                 }}
               />
             </div>
           </div>
           {activeTab === 'intro' && (
             <div className="absolute -right-1 -top-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white animate-pulse" />
           )}
         </button>
       </div>
       
       <nav className="flex-1 w-full space-y-2">
         {menuItems.map((item) => {
           const Icon = item.icon;
           const isActive = activeTab === item.id;
           return (
             <button
               key={item.id}
               onClick={() => onTabChange(item.id)}
               className={`w-full flex flex-col items-center py-2 px-1 transition-all group relative ${
                 isActive ? 'text-[#ff5c5c]' : 'text-gray-400 hover:text-gray-600'
               }`}
             >
               <div className={`p-2.5 rounded-2xl mb-1 transition-all duration-300 ${
                 isActive 
                   ? 'bg-[#ff5c5c] text-white shadow-[0_8px_16px_rgba(255,92,92,0.25)] scale-110' 
                   : 'group-hover:bg-pink-50 group-hover:text-pink-400'
               }`}>
                 <Icon size={isActive ? 22 : 20} strokeWidth={isActive ? 2.5 : 2} />
               </div>
               <span className={`text-[10px] md:text-[11px] font-bold tracking-wider ${isActive ? 'text-[#ff5c5c]' : 'text-gray-400'}`}>
                 {item.label}
               </span>
             </button>
           );
         })}
       </nav>

       {/* 用户图标 - 底部 */}
       <div className="mt-auto">
         <button
           onClick={() => onTabChange('mine')}
           className={`group relative transition-all duration-300 ${activeTab === 'mine' ? 'scale-110' : 'hover:scale-110'}`}
           title="我的"
         >
           <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 border-2 ${
             activeTab === 'mine' 
               ? 'bg-[#ff5c5c] border-[#ff5c5c] shadow-[0_8px_20px_rgba(255,92,92,0.3)]' 
               : 'bg-gradient-to-br from-pink-200 to-purple-200 border-white hover:border-pink-300 shadow-sm'
           }`}>
             <User size={activeTab === 'mine' ? 24 : 22} className={activeTab === 'mine' ? 'text-white' : 'text-gray-600'} strokeWidth={activeTab === 'mine' ? 2.5 : 2} />
           </div>
           {activeTab === 'mine' && (
             <div className="absolute -right-1 -top-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white animate-pulse" />
           )}
         </button>
       </div>
    </div>
  );
};

export default Sidebar;
